// vault-mermaid.js - Flussdiagramme (```mermaid) in der Vault-Vorschau.
//
// Die Blöcke werden VOR den Obsidian-Umwandlungen aus dem Markdown gelöst
// (wie die Mathe-Blöcke): `[[…]]`, `#tag` oder `$` im Diagrammtext würden
// sonst zu Links, Tags oder Formeln und das Diagramm wäre kaputt. An ihre
// Stelle tritt ein leerer Platzhalter, in den nach dem Rendern das SVG kommt.
// Mermaid selbst wird erst geladen, wenn eine Notiz ein Diagramm enthält.

const ZAUN = /^([ \t]*)(`{3,}|~{3,})[ \t]*mermaid[ \t]*\r?\n([\s\S]*?)\r?\n\1\2[ \t]*$/gm;

/**
 * Löst alle Mermaid-Blöcke aus dem Markdown.
 * @param {string} markdown
 * @returns {{ content: string, quellen: string[] }}
 */
export function mermaidAuslagern(markdown) {
    const quellen = [];
    const content = (markdown || '').replace(ZAUN, (_, _einzug, _zaun, quelle) => {
        const i = quellen.push(quelle) - 1;
        // Leerzeilen drumherum: marked nimmt das div nur als eigenen HTML-Block.
        return `\n<div class="vault-mermaid" data-mermaid="${i}"></div>\n`;
    });
    return { content, quellen };
}

/** Mermaid-Thema passend zum Mednos-Theme (`<html data-theme>`). */
export function mermaidThema(htmlTheme) {
    return htmlTheme === 'dark' ? 'dark' : 'default';
}

let mermaidLaden = null;
let generation = 0;
let zaehler = 0;

async function mermaidHolen() {
    mermaidLaden ??= import('mermaid').then((m) => m.default);
    return mermaidLaden;
}

/**
 * Rendert alle Platzhalter in `root`. Eine spätere Notiz (oder ein
 * Theme-Wechsel) überholt einen laufenden Durchgang: dessen Ergebnisse
 * werden verworfen statt in die neue Notiz geschrieben.
 * @param {HTMLElement} root
 * @param {string[]} [quellen] - aus `mermaidAuslagern`; fehlt es, gilt die
 *   am Platzhalter gemerkte Quelle (erneutes Rendern nach Theme-Wechsel)
 */
export async function mermaidRendern(root, quellen) {
    const plaetze = [...(root?.querySelectorAll('.vault-mermaid') || [])];
    if (!plaetze.length) return;
    for (const el of plaetze) {
        if (quellen) el.dataset.quelle = quellen[Number(el.dataset.mermaid)] ?? '';
    }

    const meine = ++generation;
    let mermaid;
    try {
        mermaid = await mermaidHolen();
    } catch (e) {
        console.error('Mermaid konnte nicht geladen werden', e);
        plaetze.forEach((el) => zeigeQuelle(el, 'Diagramm konnte nicht geladen werden.'));
        return;
    }
    if (meine !== generation) return;

    mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: mermaidThema(document.documentElement.dataset.theme),
        fontFamily: 'inherit',
        flowchart: { htmlLabels: true, useMaxWidth: true },
    });

    for (const el of plaetze) {
        const quelle = el.dataset.quelle || '';
        try {
            const { svg } = await mermaid.render(`vault-mermaid-${++zaehler}`, quelle);
            if (meine !== generation) return;
            el.innerHTML = svg;
            el.classList.remove('vault-mermaid--fehler');
        } catch (e) {
            if (meine !== generation) return;
            console.warn('Mermaid-Diagramm fehlerhaft', e);
            zeigeQuelle(el, 'Diagramm fehlerhaft — Quelltext:');
        }
    }
}

function zeigeQuelle(el, hinweis) {
    el.classList.add('vault-mermaid--fehler');
    el.replaceChildren();
    const p = document.createElement('p');
    p.textContent = hinweis;
    const pre = document.createElement('pre');
    pre.textContent = el.dataset.quelle || '';
    el.append(p, pre);
}

/** Beim Wechsel hell/dunkel die sichtbaren Diagramme neu zeichnen. */
export function mermaidThemaBeobachten(root) {
    new MutationObserver(() => mermaidRendern(root)).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
    });
}
